// Pure-JS HPKE (RFC 9180) — Base mode, suite
// `DHKEM(X25519, HKDF-SHA256), HKDF-SHA256, AES-128-GCM`
// (`kem_id=0x0020`, `kdf_id=0x0001`, `aead_id=0x0001`).
//
// This is the suite the `ping-openmls-sdk` web SDK uses for
// `MessagingClient.sealLinkingTicket` / `openLinkingTicket`
// (`/tmp/package/dist/index.cjs` line 540-ish). The RN-macOS bindings
// don't yet expose those static helpers; this module fills the gap so
// device linking works end-to-end on both platforms without dragging
// in the WebAssembly worker pipeline.
//
// !!  Wire-compat caveat  !!
// The byte layout below is the canonical RFC 9180 Base-mode encoding
// (`enc || ct`). It is intentionally library-agnostic — any RFC 9180
// implementation of the same suite will produce identical bytes for
// the same inputs (DH output is suite-fixed, HKDF labels are
// suite-fixed). That said, until QA has round-tripped a sealed ticket
// produced by the web SDK through `openSealedTicket()` on desktop AND
// vice versa, this should be treated as best-effort and not relied on
// for production keys.
//
// The clear-text payload that gets sealed is whatever the caller hands
// `seal(...)` — for linking that's the canonical CBOR encoding of the
// `LinkingTicket`. See `linkingTicketCbor.ts` for the cbor codec the
// SDK's worker.cjs uses on the inside of `wasmSealLinkingTicket`.

import { gcm } from '@noble/ciphers/aes';
import { x25519 } from '@noble/curves/ed25519';
import { expand as expandHkdf, extract as extractHkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { randomBytes } from '@noble/hashes/utils';

// --- suite constants (RFC 9180 §7.1, §7.2, §7.3) -------------------------

const KEM_ID = 0x0020; // DHKEM(X25519, HKDF-SHA256)
const KDF_ID = 0x0001; // HKDF-SHA256
const AEAD_ID = 0x0001; // AES-128-GCM (per the SDK docstring)
const VERSION_LABEL = 'HPKE-v1';
const N_H = 32; // SHA256 output
const N_K = 16; // AES-128 key
const N_N = 12; // AES-GCM nonce
const N_PK = 32; // X25519 pub
const HPKE_MODE_BASE = 0x00;

// --- labelled HKDF helpers (RFC 9180 §4.1) -------------------------------

const TE = new TextEncoder();

function i2osp(n: number, len: number): Uint8Array {
  const out = new Uint8Array(len);
  for (let i = len - 1; i >= 0 && n > 0; i--) {
    out[i] = n & 0xff;
    n >>>= 8;
  }
  return out;
}

function concat(...parts: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

function suiteIdKem(): Uint8Array {
  return concat(TE.encode('KEM'), i2osp(KEM_ID, 2));
}

function suiteIdHpke(): Uint8Array {
  return concat(
    TE.encode('HPKE'),
    i2osp(KEM_ID, 2),
    i2osp(KDF_ID, 2),
    i2osp(AEAD_ID, 2),
  );
}

// LabeledExtract(salt, label, ikm) = HKDF-Extract(salt,
//                                       "HPKE-v1" || suite_id || label || ikm)
function labeledExtract(suiteId: Uint8Array, salt: Uint8Array, label: string, ikm: Uint8Array): Uint8Array {
  const labelledIkm = concat(TE.encode(VERSION_LABEL), suiteId, TE.encode(label), ikm);
  // hkdf's extract is exposed as a helper inside @noble/hashes/hkdf
  return extractHkdf(sha256, labelledIkm, salt);
}

// LabeledExpand(prk, label, info, L) = HKDF-Expand(prk,
//                                       I2OSP(L, 2) || "HPKE-v1" || suite_id ||
//                                       label || info, L)
function labeledExpand(
  suiteId: Uint8Array,
  prk: Uint8Array,
  label: string,
  info: Uint8Array,
  length: number,
): Uint8Array {
  const labelledInfo = concat(i2osp(length, 2), TE.encode(VERSION_LABEL), suiteId, TE.encode(label), info);
  return expandHkdf(sha256, prk, labelledInfo, length);
}

// --- DHKEM(X25519, HKDF-SHA256) (RFC 9180 §7.1) --------------------------

export interface KemKeyPair {
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}

export function generateKemKeyPair(): KemKeyPair {
  // x25519 private keys are 32 random bytes (the library handles
  // RFC 7748 bit-clamping internally).
  const privateKey = randomBytes(32);
  const publicKey = x25519.getPublicKey(privateKey);
  return { publicKey, privateKey };
}

// extractAndExpand(dh, kem_context) from RFC 9180 §4.1
function kemExtractAndExpand(dh: Uint8Array, kemContext: Uint8Array): Uint8Array {
  const sid = suiteIdKem();
  const eaePrk = labeledExtract(sid, new Uint8Array(0), 'eae_prk', dh);
  return labeledExpand(sid, eaePrk, 'shared_secret', kemContext, N_H);
}

// DHKEM.Encap(pkR) returns (shared_secret, enc)
function kemEncap(pkR: Uint8Array): { sharedSecret: Uint8Array; enc: Uint8Array } {
  const ephemeral = generateKemKeyPair();
  const dh = x25519.getSharedSecret(ephemeral.privateKey, pkR);
  const enc = ephemeral.publicKey;
  const kemContext = concat(enc, pkR);
  return { sharedSecret: kemExtractAndExpand(dh, kemContext), enc };
}

// DHKEM.Decap(enc, skR) returns shared_secret
function kemDecap(enc: Uint8Array, skR: Uint8Array): Uint8Array {
  const dh = x25519.getSharedSecret(skR, enc);
  const pkR = x25519.getPublicKey(skR);
  const kemContext = concat(enc, pkR);
  return kemExtractAndExpand(dh, kemContext);
}

// --- KeySchedule (RFC 9180 §5.1) -----------------------------------------

interface AeadContext {
  key: Uint8Array;
  baseNonce: Uint8Array;
}

function keySchedule(sharedSecret: Uint8Array, info: Uint8Array): AeadContext {
  const sid = suiteIdHpke();
  // KeySchedule for Base mode: psk = ""  psk_id = ""
  const pskIdHash = labeledExtract(sid, new Uint8Array(0), 'psk_id_hash', new Uint8Array(0));
  const infoHash = labeledExtract(sid, new Uint8Array(0), 'info_hash', info);
  const keyScheduleContext = concat(new Uint8Array([HPKE_MODE_BASE]), pskIdHash, infoHash);

  const secret = labeledExtract(sid, sharedSecret, 'secret', new Uint8Array(0));
  const key = labeledExpand(sid, secret, 'key', keyScheduleContext, N_K);
  const baseNonce = labeledExpand(sid, secret, 'base_nonce', keyScheduleContext, N_N);
  return { key, baseNonce };
}

// --- public API ----------------------------------------------------------

/**
 * HPKE-Base seal: encrypts `plaintext` for the holder of `recipientPub`.
 * Returns `enc || ciphertext` — the 32-byte ephemeral KEM pubkey
 * concatenated with the AEAD output (ciphertext || 16-byte auth tag).
 *
 * `info` is bound into the key schedule; both ends MUST agree on it.
 * For Ping linking the SDK uses an empty info, but expose it here for
 * future-proofing.
 */
export function seal(
  recipientPub: Uint8Array,
  plaintext: Uint8Array,
  info: Uint8Array = new Uint8Array(0),
): Uint8Array {
  if (recipientPub.length !== N_PK) {
    throw new Error(`hpke.seal: recipient pubkey must be ${N_PK} bytes, got ${recipientPub.length}`);
  }
  const { sharedSecret, enc } = kemEncap(recipientPub);
  const { key, baseNonce } = keySchedule(sharedSecret, info);
  // RFC 9180 single-shot Seal: seq = 0, so nonce = base_nonce ^ 0 = base_nonce.
  const ct = gcm(key, baseNonce).encrypt(plaintext);
  return concat(enc, ct);
}

/**
 * HPKE-Base open: decrypts a payload produced by [[seal]]. `sealed`
 * is the `enc || ct` concatenation. `info` must match the value the
 * sender bound into the key schedule (empty for Ping linking).
 *
 * Throws a deliberately generic error on AEAD failure — no
 * discrimination between "wrong key" and "tampered ciphertext" so
 * a probing attacker can't tell which one tripped.
 */
export function open(
  recipientPriv: Uint8Array,
  sealed: Uint8Array,
  info: Uint8Array = new Uint8Array(0),
): Uint8Array {
  if (recipientPriv.length !== 32) {
    throw new Error(`hpke.open: recipient privkey must be 32 bytes, got ${recipientPriv.length}`);
  }
  if (sealed.length <= N_PK) {
    throw new Error('hpke.open: sealed bytes too short');
  }
  const enc = sealed.subarray(0, N_PK);
  const ct = sealed.subarray(N_PK);
  const sharedSecret = kemDecap(enc, recipientPriv);
  const { key, baseNonce } = keySchedule(sharedSecret, info);
  try {
    return gcm(key, baseNonce).decrypt(ct);
  } catch {
    throw new Error('hpke.open: decryption failed');
  }
}
